import { mutate } from "swr";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

const baseUrl =
  process.env.NODE_ENV === "production"
    ? "https://wednesday-server-m3dlsop5cq-de.a.run.app"
    : "http://localhost:3031";

/** Lark logged-in user (backend user object, loose fields, extend as needed) */
export interface User {
  id: string;
  name?: string;
  avatarThumb?: string;
  email?: string;
  createdAt?: string;
  /** functional roles (enum: Roles below), for people-picker filtering only */
  roles?: string[];
  isAdmin?: boolean;
  /** human = Feishu OAuth person; bot = app-level bot account (token login) */
  kind?: "human" | "bot";
  /** legacy: pre-roles localStorage payloads; only useIsAdmin's fallback reads it */
  permissions?: string[];
  visibleApps?: {
    id: string | number;
    name?: string;
    code?: string | number | null;
    archived?: boolean;
    [key: string]: unknown;
  }[];
  [key: string]: unknown;
}

/** Auth state persisted in localStorage (auth_v2) */
export interface Auth {
  id?: string;
  user?: User;
  [key: string]: unknown;
}

interface AuthState {
  auth: Auth;
  setToken: (auth: Auth) => void;
  removeToken: () => void;
}

export const useAuthStore = create<AuthState>()(
  devtools((set) => ({
    auth: JSON.parse(localStorage.getItem("auth_v2") || "{}") as Auth,
    setToken: (auth: Auth) => {
      localStorage.setItem("auth_v2", JSON.stringify(auth));
      set({ auth });
    },
    removeToken: () => {
      localStorage.removeItem("auth_v2");
      set({ auth: {} as Auth });
    },
  }))
);

// server 侧权威枚举在 server/src/user/dto/update-user.dto.ts 的 ROLE_VALUES,同步改
export const Roles = {
  developer: "developer",
  tester: "tester",
  pm: "pm",
  designer: "designer",
  operation: "operation",
  creative: "creative",
  advertiser: "advertiser",
  hr: "hr",
  finance: "finance",
  illustrator: "illustrator",
  monetization: "monetization",
} as const;

export type RoleValue = (typeof Roles)[keyof typeof Roles];

export const ROLES_LIST: RoleValue[] = [
  Roles.developer,
  Roles.tester,
  Roles.pm,
  Roles.designer,
  Roles.operation,
  Roles.creative,
  Roles.advertiser,
  Roles.hr,
  Roles.finance,
  Roles.illustrator,
  Roles.monetization,
];

/** 角色中文名(展示用;存储/筛选仍用英文值) */
export const ROLE_LABELS: Record<RoleValue, string> = {
  developer: "开发",
  tester: "测试",
  pm: "产品",
  designer: "UI",
  operation: "运营",
  creative: "投放素材",
  advertiser: "投放师",
  hr: "HR",
  finance: "财务",
  illustrator: "画师",
  monetization: "商业化",
};

/** 角色展示名:未知值(历史数据)原样回退 */
export const roleLabel = (role: string): string =>
  ROLE_LABELS[role as RoleValue] ?? role;

/**
 * 按职能角色分组(ROLES_LIST 顺序):一人多角色会在每个角色组出现一次;
 * 没有任何已知角色的归入「未设置」;空组不返回。App 设置页 Members 与
 * Admin Users 列表共用。
 */
export const groupUsersByRole = <T extends { roles?: string[] }>(
  users: T[]
) => {
  const groups = ROLES_LIST.map((role) => ({
    key: role as string,
    label: roleLabel(role),
    users: users.filter((u) => (u.roles ?? []).includes(role)),
  })).filter((g) => g.users.length > 0);
  const others = users.filter(
    (u) => !(u.roles ?? []).some((r) => (ROLES_LIST as string[]).includes(r))
  );
  if (others.length) groups.push({ key: "none", label: "未设置", users: others });
  return groups;
};

/** Error carrying an HTTP status code */
export interface HttpError extends Error {
  status?: number;
  /** Parsed JSON error body, for callers that branch on structured errors. */
  data?: unknown;
}

type Params = Record<string, unknown> | null | undefined;

const urlWrpper = async (
  endpoint: string,
  params: Params = {},
  options: RequestInit = {}
): Promise<Response> => {
  const xUserId = useAuthStore.getState().auth?.id;

  const url = new URL(`${baseUrl}${endpoint}`);
  if (params) {
    const validParams = assembleParams(params);
    Object.keys(validParams).forEach((key) =>
      url.searchParams.append(key, validParams[key])
    );
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(xUserId && { "x-user-id": xUserId }),
    ...(options.headers as Record<string, string>), // Add custom headers
  };
  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    if (response.status === 401) {
      // Token might be expired, handle token refresh or redirect to login
      useAuthStore.getState().removeToken();
      // Optionally redirect to login page or refresh token
    }
    // Try to extract the error message from the response
    let errorMessage = "Failed to update data";
    let errorData: unknown;
    try {
      errorData = await response.json();
      const message = (errorData as { message?: unknown } | null)?.message;
      // class-validator 400s send `message` as a string array
      const text = Array.isArray(message)
        ? message.filter((m): m is string => typeof m === "string").join("; ")
        : message;
      if (typeof text === "string" && text) {
        errorMessage = text;
      }
    } catch {
      // If the response is not JSON, use the default error message
    }
    const error = new Error(errorMessage) as HttpError;
    error.status = response.status;
    error.data = errorData;
    throw error;
  }
  return response;
};

type MutationArg<T = unknown> = { arg: T };

/**
 * Multipart attachment upload (field name `file`; images or text/html) —
 * bypasses urlWrpper because FormData must set its own multipart boundary
 * Content-Type. Returns the stable GCS reference URL for markdown; the bucket
 * is private, so rendering swaps it for a signed URL (see
 * components/prose-markdown).
 */
export const uploadAssetFile = async (
  file: File
): Promise<{ url: string; objectKey: string }> => {
  const xUserId = useAuthStore.getState().auth?.id;
  const fd = new FormData();
  fd.append("file", file);
  const response = await fetch(`${baseUrl}/uploads`, {
    method: "POST",
    headers: { ...(xUserId && { "x-user-id": xUserId }) },
    body: fd,
  });
  if (!response.ok) {
    let message = `Upload failed (${response.status})`;
    try {
      const data = await response.json();
      if (data?.message) message = data.message;
    } catch {
      // non-JSON error body — keep the default message
    }
    const error = new Error(message) as HttpError;
    error.status = response.status;
    throw error;
  }
  return response.json();
};

export const tokenFetcher = async (
  endpoint: string,
  { arg }: MutationArg
): Promise<{ id: string; user: User }> => {
  const url = new URL(`${baseUrl}${endpoint}`);

  const headers = {
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "POST",
    headers,
    credentials: "include", // include credentials
    body: JSON.stringify(arg),
  });
  if (!response.ok) {
    if (response.status === 401) {
      useAuthStore.getState().removeToken();
    }
    const errorData = await response.json();
    throw errorData;
  }
  const res = await response.json();
  const { id: xid, user } = res;
  useAuthStore.getState().setToken({ id: xid, user });
  return res;
};

export const delFetcher = async <T = unknown>(
  endpoint: string,
  { arg }: MutationArg<string>
): Promise<T> => {
  const url = `${endpoint}/${arg}`;

  const response = await urlWrpper(url, null, { method: "DELETE" });
  return response.json();
};

/**
 * DELETE the endpoint itself — delFetcher appends `arg` as a path segment,
 * which doesn't fit routes like DELETE /prds/:id/follow. `#tag` key suffixes
 * are stripped like putFetcherKeyed.
 */
export const deleteFetcherKeyed = async <T = unknown>(
  endpoint: string
): Promise<T> => {
  const response = await urlWrpper(endpoint.split("#")[0], null, {
    method: "DELETE",
  });
  return response.json();
};

export const postFetcher = async <T = unknown>(
  endpoint: string,
  { arg }: MutationArg,
  options: RequestInit = {}
): Promise<T> => {
  const response = await urlWrpper(endpoint, null, {
    ...options,
    method: "POST",
    body: JSON.stringify(arg),
  });
  return response.json();
};

/**
 * putFetcher with a distinct mutation key: useSWRMutation shares isMutating
 * per key, so several buttons mutating the same URL (`/prds/:id`) would all
 * gray out together. Append `#tag` to the key to isolate them — the suffix is
 * stripped before the request.
 */
export const putFetcherKeyed = async <T = unknown>(
  endpoint: string,
  arg: MutationArg,
  options: RequestInit = {}
): Promise<T> => putFetcher<T>(endpoint.split("#")[0], arg, options);

export const putFetcher = async <T = unknown>(
  endpoint: string,
  { arg }: MutationArg,
  options: RequestInit = {}
): Promise<T> => {
  const response = await urlWrpper(endpoint, null, {
    ...options,
    method: "PUT",
    body: JSON.stringify(arg),
  });
  return response.json();
};

export const fetcher = async <T = unknown>([
  endpoint,
  params = {},
  options = {},
]: [string, Params?, RequestInit?]): Promise<T> => {
  const response = await urlWrpper(endpoint, params, options);
  return response.json();
};

/**
 * Normalize query params the way the URL will carry them: drop
 * `undefined`/`null`/`""`, sort by name, stringify values. Shared by the
 * fetcher (URL building) and `swrKey` so one URL maps to exactly one cache
 * entry — SWR's key hash ignores `undefined` but not `""`/`null`, and hashes
 * `1` and `"1"` differently (WED-250).
 */
const normalizeParams = (params: Record<string, unknown>): Record<string, string> =>
  Object.fromEntries(
    Object.entries(params)
      .filter(([, value]) => value != null && value !== "")
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => [k, String(v)])
  );

const assembleParams = normalizeParams;

export type SwrKey = [string] | [string, Record<string, string>];

/**
 * Build a `useSWR` / `mutate` key for `endpoint` + optional query params with
 * the same normalization as the request URL. No (effective) params →
 * `[endpoint]`, matching the existing bare-key style (e.g. `[INBOX_KEY]`).
 */
export const swrKey = (endpoint: string, params?: object | null): SwrKey => {
  if (!params) return [endpoint];
  const normalized = normalizeParams(params as Record<string, unknown>);
  return Object.keys(normalized).length ? [endpoint, normalized] : [endpoint];
};

/**
 * Revalidate every cached key whose path is `path`, keeping the current data on
 * screen (stale-while-revalidate). Pass the filter only: `mutate(filter,
 * undefined, …)` writes `undefined` into the cache first (populateCache
 * defaults to true), flipping mounted hooks back to isLoading (WED-248).
 */
export const mutateRequest = (path: string) =>
  mutate((key) => Array.isArray(key) && (
    key[0] === path || (path === "/engagements" && key[0] === "/engagements/wish-pool")
  ));

/** Paged response wrapper: { data: T[], total } or a bare array */
interface Paged<T> {
  data: T[];
  total?: number;
  [key: string]: unknown;
}

/** RC-style wrapper: { list: T[], total } */
interface Listed<T> {
  list: T[];
  total?: number;
  [key: string]: unknown;
}

type Identifiable = { id?: string | number; [key: string]: unknown };
type ListData<T> = Paged<T> | Listed<T> | T[];

/** Cache keys are `[path, params?]`: match every entry whose path is `path`. */
const keyAt = (path: string) => (key: unknown) =>
  Array.isArray(key) && key[0] === path;

/**
 * Rewrite the items of every cached list at `path`, keeping its envelope
 * ({ data, total } / { list, total } / bare array) and bumping `total` by
 * `delta`. Cache-only: no request is made.
 */
const updateList = <T extends Identifiable>(
  path: string,
  fn: (items: T[]) => T[],
  delta = 0
) =>
  mutate(
    keyAt(path),
    (cur: ListData<T> | undefined) => {
      if (!cur) return cur;
      if (Array.isArray(cur)) return fn(cur);
      const total =
        typeof cur.total === "number" ? Math.max(0, cur.total + delta) : cur.total;
      if (Array.isArray((cur as Paged<T>).data))
        return { ...cur, data: fn((cur as Paged<T>).data), total };
      if (Array.isArray((cur as Listed<T>).list))
        return { ...cur, list: fn((cur as Listed<T>).list), total };
      return cur;
    },
    { revalidate: false }
  );

// Write-back helpers (WED-249): after a successful write, put the server's
// response into the cache instead of refetching whole lists. Pair them with
// `revalidate: false`; on error, `mutateRequest(path)` restores the truth.

/** Merge `patch` into the list item with `id` (every cached list at `path`). */
export const patchListItem = (
  path: string,
  id: string | number,
  patch: Record<string, unknown>
) =>
  updateList<Identifiable>(path, (items) =>
    items.map((item) => (item.id === id ? { ...item, ...patch } : item))
  );

/** Add `item` to every cached list at `path` (end by default; `prepend` for newest-first lists). */
export const appendListItem = (
  path: string,
  item: Identifiable,
  { prepend = false }: { prepend?: boolean } = {}
) =>
  updateList<Identifiable>(
    path,
    (items) => (prepend ? [item, ...items] : [...items, item]),
    1
  );

/** Drop the item with `id` from every cached list at `path`. */
export const removeListItem = (path: string, id: string | number) =>
  updateList<Identifiable>(
    path,
    (items) => items.filter((item) => item.id !== id),
    -1
  );

/**
 * Replace the cached object at `path` (a detail endpoint) with `data`, or
 * derive it from the current value. Entries that aren't cached are left alone.
 */
export const writeAtom = <T>(
  path: string,
  data: T | ((cur: T) => T)
) =>
  mutate(
    keyAt(path),
    (cur: T | undefined) =>
      cur === undefined
        ? cur
        : typeof data === "function"
          ? (data as (cur: T) => T)(cur)
          : data,
    { revalidate: false }
  );

export const optimisticList = patchListItem;

export const optimisticAtom = (
  url: string,
  newData: Record<string, unknown>
) => {
  mutate(
    keyAt(url),
    (atom: Record<string, unknown> | undefined) => ({ ...atom, ...newData }),
    {
      revalidate: false,
    }
  );
};

export const optimisticPrds = (id: string, newData: Record<string, unknown>) =>
  optimisticList("/prds", id, newData);

export const optimisticPrd = (id: string, newData: Record<string, unknown>) =>
  optimisticAtom(`/prds/${id}`, newData);

export const optimisticEngagements = (id: string, newData: Record<string, unknown>) =>
  optimisticList("/engagements", id, newData);

